import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCover } from "@/components/ProductCover";

const formatPrice = (c) => `€${(c / 100).toFixed(2)}`;

const BuyChip = ({ p, owned, onBuy }) => {
  if (owned) return <span data-testid={`saved-owned-${p.product_id}`} className="shrink-0 h-8 px-3 rounded-full bg-[#1E1E1E] text-xs text-[#9CA3AF] flex items-center">Owned</span>;
  if (!onBuy) return null;
  const free = p.free_for_me || p.price_cents === 0;
  return (
    <button type="button" onClick={onBuy} data-testid={`saved-buy-${p.product_id}`}
      className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium text-white transition-colors ${free ? "bg-[#10B981] hover:bg-[#059669]" : "bg-[#2563EB] hover:bg-[#1D4ED8]"}`}>
      {free ? "Get free" : `Buy ${formatPrice(p.price_cents)}`}
    </button>
  );
};

export const CommunitySaved = ({ onClose, onBuy, owned }) => {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/saved").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const remove = async (pid) => {
    setList((l) => l.filter((p) => p.product_id !== pid));
    await api.delete(`/products/${pid}/save`).catch(() => {});
    window.dispatchEvent(new CustomEvent("notelosh:saved-changed"));
  };
  const open = (p) => { onClose(); navigate(p.share_code ? `/market?code=${p.share_code}` : `/market?product=${p.product_id}`); };

  if (!list) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6B7280]" /></div>;
  if (!list.length) return (
    <div data-testid="saved-empty" className="px-6 py-16 text-center">
      <Bookmark className="h-6 w-6 mx-auto mb-2 text-[#6B7280]" />
      <div className="text-sm font-medium text-[#F3F4F6] mb-1">No saved notes yet</div>
      <div className="text-xs text-[#9CA3AF]">Tap the bookmark on any note in the Marketplace to keep it here.</div>
    </div>
  );
  return (
    <ul className="divide-y divide-[#1E1E1E]" data-testid="saved-list">
      {list.map((p) => (
        <li key={p.product_id} className="flex items-center gap-3 px-5 py-3 hover:bg-[#121212] transition-colors">
          <button type="button" onClick={() => open(p)} data-testid={`saved-item-${p.product_id}`} className="flex items-center gap-3 min-w-0 flex-1 text-left">
            <div className="h-11 w-11 shrink-0 rounded-lg overflow-hidden bg-[#1E1E1E]"><ProductCover p={p} /></div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{p.name}</div>
              <div className="text-[11px] text-[#9CA3AF] truncate">{p.seller_name} · {p.price_cents === 0 ? "Free" : formatPrice(p.price_cents)}</div>
            </div>
          </button>
          <BuyChip p={p} owned={p.owned_by_me || owned?.has(p.product_id)} onBuy={onBuy && (() => { onClose(); onBuy(p); })} />
          <button type="button" onClick={() => remove(p.product_id)} data-testid={`saved-remove-${p.product_id}`} aria-label="Remove from saved"
            className="h-8 w-8 rounded-full flex items-center justify-center text-[#6B7280] hover:text-white hover:bg-[#1E1E1E]"><X className="h-4 w-4" /></button>
        </li>
      ))}
    </ul>
  );
};
